import { el, clear } from "../components/dom.js";
import { getBookSeries, searchBooks } from "../api/books.js";
import { addBook, listBooks, deleteBookByOlId, getBookByOlId } from "../db/books.js";
import { createManualGroup, listGroups, addItemToGroup, updateGroup } from "../db/groups.js";
import { navigate, navigateBack } from "../main.js";
import {
	MAG,
	magFeatureHero,
	magGlass,
	magButton,
	magEpisodeRow,
	magEmpty,
	magError,
	magLoading,
} from "../components/magazine/kit.js";

// Look up a book SERIES (Wikidata-ordered) and present its books like episodes:
// each row is Add / Have. Adding a book also drops it into a Collection named
// after the series, so a saga lands on your "shelf" as one group (the link
// between the book-series feature and the collections redesign).
export async function renderBookSeries(root, _match, wdId) {
	const view = el("div.view", {});
	root.append(view);

	view.append(magLoading({ shape: "hero" }), magLoading({ shape: "list" }));

	let series;
	try {
		series = await getBookSeries(wdId);
	} catch (err) {
		clear(view);
		view.append(magError({ title: "Couldn't load series", onRetry: () => renderBookSeries(root, _match, wdId) }));
		return;
	}

	if (!series || (series.books ?? []).length === 0) {
		clear(view);
		view.append(
			el("div", { style: { paddingTop: "8px" } },
				magButton("‹ Back", { variant: "ghost", onClick: () => navigateBack("/books/add") })),
			magEmpty({
				title: "No books found",
				subtitle: "Wikidata doesn't have an ordered book list for this series yet. Try the Title or Author search instead.",
				actionLabel: "Back to search",
				onAction: () => navigateBack("/books/add"),
			}),
		);
		return;
	}

	await mount(view, series);
}

async function mount(view, series) {
	clear(view);

	const mine = await listBooks();
	const ownedByOlId = new Set(mine.map((b) => b.ol_id));
	const readByOlId = new Set(mine.filter((b) => b.engagement_status === "done").map((b) => b.ol_id));
	const isOwned = (b) => b.ol_id != null && ownedByOlId.has(b.ol_id);
	const isDone = (b) => b.ol_id != null && readByOlId.has(b.ol_id);
	const ownedCount = series.books.filter(isOwned).length;
	const doneCount = series.books.filter(isDone).length;
	const total = series.books.length;
	const list_ = series.books;

	// Series completion (roadmap #39 / #40): finished so far, and what's next in order.
	const nextUp = list_.find((x) => !isDone(x));
	const completion = el("div", { style: { padding: "12px 16px 0" } },
		el("div", { style: { display: "flex", justifyContent: "space-between", fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px", color: MAG.ink } },
			el("span", {}, `${doneCount} of ${total} read`),
			el("span", { style: { color: MAG.lime } }, total ? `${Math.round((doneCount / total) * 100)}%` : "")),
		el("div", { style: { height: "6px", borderRadius: "6px", background: "rgba(255,255,255,.1)", overflow: "hidden", marginTop: "6px" } },
			el("div", { style: { width: `${total ? (doneCount / total) * 100 : 0}%`, height: "100%", background: MAG.lime } })),
		nextUp && doneCount
			? el("div", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "11.5px", color: MAG.dim, marginTop: "6px" } }, `Next up: ${nextUp.title}`)
			: "");


	// ---- hero ----
	const coverItem = {
		media_type: "book",
		cover_url: series.books.find((b) => b.cover_url)?.cover_url ?? null,
	};

	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px",
			letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, padding: "10px 16px 0",
		},
	});

	async function addOne(book) {
		statusLine.textContent = `Adding ${book.title}…`;
		try {
			const record = await resolveAndAdd(book);
			if (!record) {
				statusLine.textContent = `Couldn't find "${book.title}" in Open Library.`;
				return null;
			}
			await ensureInCollection(series, record.id);
			return record;
		} catch (err) {
			statusLine.textContent = `Could not add: ${err.message}`;
			return null;
		}
	}

	const addAllBtn = magButton(`+ Add all (${total - ownedCount})`, {
		variant: ownedCount >= total ? "owned" : "primary",
		onClick: async () => {
			addAllBtn.disabled = true;
			for (const book of series.books) {
				if (!isOwned(book)) await addOne(book);
			}
			await mount(view, series);
		},
	});

	const bar = magGlass(
		el("div", { style: { display: "flex", alignItems: "center", gap: "12px", width: "100%" } },
			el("div", { style: { flex: "1", minWidth: "0" } },
				el("div", { style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", color: "#fff", lineHeight: ".9" } },
					`${total} book${total === 1 ? "" : "s"}`),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
						letterSpacing: ".1em", textTransform: "uppercase", color: MAG.lime, marginTop: "3px",
					},
				}, `${ownedCount} in your library`),
			),
			addAllBtn,
		),
		{ variant: "over-art", style: { padding: "12px 14px" } },
	);

	const hero = magFeatureHero(coverItem, {
		height: "240px",
		kicker: series.author ? `Book series · ${series.author}` : "Book series",
		title: series.title ?? "Series",
		onBack: () => navigateBack("/books/add"),
		bar,
	});

	// ---- collection note ----
	const note = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "10px",
			letterSpacing: ".04em", color: MAG.faint, padding: "12px 16px 0",
		},
	}, `Adds land in a collection: ${series.title ?? "this series"}`);

	// ---- ordered list ----
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "12px 16px 24px" } });
	series.books.forEach((book, i) => {
		const owned = isOwned(book);
		const yr = book.published_year ? ` · ${book.published_year}` : "";
		list.append(
			magEpisodeRow(
				{ title: book.title, cover_url: book.cover_url, media_type: "book" },
				{
					number: i + 1,
					kicker: `Book ${i + 1}${yr}`,
					owned,
					ownedLabel: "Have",
					addLabel: "+ Add",
					onAdd: async () => {
						if (owned) {
							// Tapping "Have" removes it again.
							if (book.ol_id) await deleteBookByOlId(book.ol_id);
						} else {
							await addOne(book);
						}
						await mount(view, series);
					},
				},
			),
		);
	});

	view.append(hero, completion, note, statusLine, list);
}

// Add a series book to the library AND into the series' collection. Reused by
// the collection view (group-detail) to add a "not yet added" suggestion. The
// series object only needs { wd_id, title }.
export async function addSeriesBookToGroup(series, book) {
	const record = await resolveAndAdd(book);
	if (!record) return null;
	await ensureInCollection(series, record.id);
	return record;
}

// Add a series book to the library. Wikidata gives us an Open Library work id
// (P648) for many books; when present we add directly. When missing, fall back
// to a title search to resolve a work id + metadata.
async function resolveAndAdd(book) {
	if (book.ol_id && /^OL\d+W$/.test(book.ol_id)) {
		const existing = await getBookByOlId(book.ol_id);
		if (existing) return existing;
		return addBook({
			ol_id: book.ol_id,
			title: book.title,
			published_year: book.published_year ?? null,
			cover_url: book.cover_url ?? null,
			authors: [],
			subjects: [],
		});
	}
	// Resolve via a title search.
	const results = await searchBooks(book.title, { field: "title", limit: 5 });
	const hit = (results ?? []).find((r) => r.ol_id);
	if (!hit) return null;
	const existing = await getBookByOlId(hit.ol_id);
	if (existing) return existing;
	return addBook({
		ol_id: hit.ol_id,
		title: hit.title ?? book.title,
		authors: hit.authors ?? [],
		published_year: hit.published_year ?? book.published_year ?? null,
		cover_url: hit.cover_url ?? book.cover_url ?? null,
		subjects: hit.subjects ?? [],
	});
}

// Reuse a same-named book collection if one exists, else create it. Tag it with
// the Wikidata series id so the collection view can show the rest of the series
// as dimmed suggestions.
async function ensureInCollection(series, itemId) {
	const name = series.title ?? "Series";
	const groups = await listGroups();
	let group = groups.find((g) => g.media_type === "book" && (g.name ?? "").toLowerCase() === name.toLowerCase());
	if (!group) {
		await createManualGroup({ name, media_type: "book", member_ids: [itemId], series_ref: series.wd_id ?? null });
		return;
	}
	if (!group.series_ref && series.wd_id) {
		await updateGroup(group.id, { series_ref: series.wd_id });
	}
	await addItemToGroup(group.id, itemId);
}
